import { OverflowTooltip, TableColumn } from '@backstage/core-components';
import { coreExtensionData } from '@backstage/frontend-plugin-api';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import { EntityCardBlueprint } from '@backstage/plugin-catalog-react/alpha';
import catalogPlugin, {
  CatalogIndexPage,
} from '@backstage/plugin-catalog/alpha';
import {
  annotationOf,
  hasCommitActivity,
  LastCommit,
  lastCommitAnnotation,
} from './commitActivity';

const descriptionField = 'entity.metadata.description';
const tagsField = 'entity.metadata.tags';

const lastCommitOf = ({ entity }: CatalogTableRow) =>
  annotationOf({ entity, annotation: lastCommitAnnotation });

const lastCommitColumn: TableColumn<CatalogTableRow> = {
  title: 'Last commit',
  render: ({ entity }) => <LastCommit entity={entity} />,
  // ISO 8601 times sort correctly as strings.
  customSort: (a, b) =>
    (lastCommitOf(a) ?? '').localeCompare(lastCommitOf(b) ?? ''),
};

// The default description column clips the text to one line. Five lines keeps rows short.
// The table has no tags column. Tags stay on the entity page and in the tag filter.
// Only components get commit activity, because the backend adds it only to components.
// The table shows only the last commit. The commit counts are on the entity page.
const columns: CatalogTableColumnsFunc = context => {
  // The filter comes first, because the entities are stale while a new kind loads.
  const kind = (
    context.filters.kind?.value ?? context.entities[0]?.kind
  )?.toLowerCase();
  return CatalogTable.defaultColumnsFunc(context).flatMap(column => {
    if (column.field === tagsField) {
      return [];
    }
    if (column.field !== descriptionField) {
      return [column];
    }
    const description: TableColumn<CatalogTableRow> = {
      ...column,
      render: ({ entity }) => (
        <OverflowTooltip text={entity.metadata.description} line={5} />
      ),
      cellStyle: { minWidth: 240 },
    };
    return kind === 'component'
      ? [description, lastCommitColumn]
      : [description];
  });
};

// The page extension has no input for columns, so replace its loader.
const indexPage = catalogPlugin.getExtension('page:catalog').override({
  factory(originalFactory, { inputs, config }) {
    return originalFactory({
      params: {
        loader: async () => (
          <CatalogIndexPage
            filters={
              <>
                {inputs.filters.map(f => f.get(coreExtensionData.reactElement))}
              </>
            }
            pagination={config.pagination}
            columns={columns}
          />
        ),
      },
    });
  },
});

// The About card has no input for extra fields, so commit activity is a card of its own.
const commitActivityCard = EntityCardBlueprint.make({
  name: 'commit-activity',
  params: {
    type: 'info',
    filter: hasCommitActivity,
    loader: async () =>
      import('./CommitActivityCard').then(m => <m.CommitActivityCard />),
  },
});

export const catalogWithFullDescriptions = catalogPlugin.withOverrides({
  extensions: [indexPage, commitActivityCard],
});
